'use client';

import { useSyncExternalStore } from 'react';
import SparklesIcon from '@mui/icons-material/AutoAwesome';
import { Alert, Box, Button, IconButton, Typography } from '@mui/material';

import { getPersistedReferralCode, useAuth, useSignInHandlers, useT } from '@/hooks';

/**
 * ReferralRibbon Component
 * Displays a persistent, high-visibility ribbon at the top of the application
 * when a referral code is active in the user's session.
 */
export const ReferralRibbon = () => {
  const { isAuthenticated, isLoading } = useAuth();
  const { t } = useT();

  const code = useSyncExternalStore(
    (callback) => {
      window.addEventListener('storage', callback);
      return () => window.removeEventListener('storage', callback);
    },
    () => getPersistedReferralCode(),
    () => null
  );

  const { onLogin } = useSignInHandlers(() => {});

  if (!code || isAuthenticated || isLoading) return null;

  return (
    <Alert
      icon={false}
      severity="info"
      sx={(theme) => ({
        borderRadius: 0,
        py: 1.3,
        px: { xs: 1, sm: 4 },
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
        color: 'common.white',
        border: 'none',
        boxShadow: `0 2px 10px ${theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.5)' : 'rgba(0,0,0,0.15)'}`,
        zIndex: 1201,
        '& .MuiAlert-message': {
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: { xs: 1, sm: 1 },
          padding: 0,
        },
      })}
    >
      <SparklesIcon
        fontSize="small"
        sx={{ color: 'warning.light', display: { xs: 'none', md: 'block' } }}
      />
      <Typography
        variant="body2"
        sx={{
          fontWeight: 600,
          fontSize: { xs: '0.75rem', sm: '0.875rem' },
          color: 'common.white',
          textAlign: 'center',
          letterSpacing: '0.01em',
        }}
      >
        {t('referral.ribbonWelcome')}{' '}
        <Box component="span" sx={{ fontWeight: 800, textDecoration: 'underline' }}>
          {code}
        </Box>{' '}
        {t('referral.ribbonActive')}
      </Typography>
      <Button
        size="small"
        variant="contained"
        onClick={onLogin}
        sx={{
          bgcolor: 'common.white',
          color: 'primary.dark',
          fontWeight: 800,
          fontSize: { xs: '0.7rem', sm: '0.75rem' },
          px: { xs: 2.5, sm: 3.5 },
          py: 0.75,
          borderRadius: '8px',
          textTransform: 'none',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          '&:hover': {
            bgcolor: 'background.paper',
            color: 'primary.main',
            transform: 'translateY(-1px)',
            boxShadow: '0 6px 16px rgba(0,0,0,0.2)',
          },
          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      >
        {t('referral.ribbonJoin')}
      </Button>
    </Alert>
  );
};

export default ReferralRibbon;
